import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import isValidDomain from 'is-valid-domain'

import { Button, Field, Label, Modal, SearchBox, SimpleInput, TextArea, UploadInput } from '@makyoapp/makyo-ui'

import useModal from '@/hooks/useModal'

import { GenerateAppType } from '../OrgAddNewAppPage'
import LoadingGenerateNewAppModal from './LoadingGenerateNewAppModal'

// TODO: Replace this with actual data
const templateList = [
  { id: 1, name: 'Template 1', image: 'https://space-kd.sgp1.cdn.digitaloceanspaces.com/media/IMG_2762.webp' },
  { id: 2, name: 'Template 2', image: 'https://space-kd.sgp1.cdn.digitaloceanspaces.com/media/IMG_2763.webp' },
  { id: 3, name: 'Template 3', image: 'https://space-kd.sgp1.cdn.digitaloceanspaces.com/media/IMG_2764.webp' },
  { id: 4, name: 'Template 4', image: 'https://space-kd.sgp1.cdn.digitaloceanspaces.com/media/IMG_1234.webp' },
  { id: 5, name: 'Template 5', image: 'https://space-kd.sgp1.cdn.digitaloceanspaces.com/media/IMG_2765.webp' },
  { id: 6, name: 'Template 6', image: 'https://space-kd.sgp1.cdn.digitaloceanspaces.com/media/IMG_1235.webp' },
  { id: 7, name: 'Template 7', image: 'https://space-kd.sgp1.cdn.digitaloceanspaces.com/media/IMG_2768.webp' },
  { id: 8, name: 'Template 8', image: 'https://space-kd.sgp1.cdn.digitaloceanspaces.com/media/IMG_2769.webp' },
]

interface GenerateNewAppModalProps {
  showAdd: boolean
  closeModal: () => void
  type: GenerateAppType
}

interface TemplateProps {
  id: number
  name: string
  image: string
}

const appGeneratorData = {
  ecommerce: { title: 'Templates', placeholder: 'Select your products' },
  text: { title: 'Create your app from text', placeholder: 'Paste your text here' },
  database: { title: 'Upload Product Database', placeholder: 'Select your products' },
  website: { title: 'Turn a website to an app', placeholder: 'e.g makyo.co' },
  template: { title: 'Create your app from template', placeholder: 'Select your template' },
}

interface TemplateFormProps {
  search: string
  templates: TemplateProps[]
  handleSearch: (search: string) => void
}

function TemplateForm({ search, templates, handleSearch }: TemplateFormProps) {
  const { t } = useTranslation()
  return (
    <>
      <div className='flex items-center gap-2 max-w-sm'>
        <SearchBox value={search} id='search-templates' onSearch={handleSearch} placeholder={t('Search Templates')} />
        <Button>Search</Button>
      </div>
      <div className='mt-4'>
        <div className='grid grid-cols-4 gap-6'>
          {templates.map((template) => (
            <div
              key={template.id}
              className='relative rounded-md shadow-sm hover:shadow-lg cursor-pointer ring-1 ring-gray-200 overflow-hidden group'
            >
              <div className='absolute inset-0 bg-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-100 flex items-center justify-center'>
                <Button>{t('Use Template')}</Button>
              </div>
              <img src={template.image} alt='template' className='group-hover:opacity-100 opacity-35' />
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

interface FormProps {
  toggleVisibility: () => void
}

function WebsiteForm({ toggleVisibility }: FormProps) {
  const { t } = useTranslation()
  const [website, setWebsite] = useState<string>('')

  const isValidDomainOrUrl = (input: string) => {
    try {
      const domain = new URL(input).hostname
      return isValidDomain(domain)
    } catch {
      return isValidDomain(input)
    }
  }

  return (
    <>
      <Field isVertical className='-mt-4'>
        <Label>{t('Website URL')}</Label>
        <SimpleInput value={website} onChange={(e) => setWebsite(e.target.value)} placeholder='e.g makyo.co' />
      </Field>
      <Button onClick={toggleVisibility} disabled={!website || !isValidDomainOrUrl(website)} className='mt-1'>
        {t('Convert to App')}
      </Button>
    </>
  )
}

function DatabaseForm({ toggleVisibility }: FormProps) {
  const { t } = useTranslation()
  const [file, setFile] = useState<string>('')

  return (
    <>
      <Field isVertical className='-mt-4'>
        <Label className='md:w-full'>{t('Convert your database into an app')}</Label>
        <UploadInput placeholder='Select your database' onChange={(e) => setFile(e.target.value)} />
      </Field>
      <Button className='mt-1' onClick={toggleVisibility} disabled={!file}>
        {t('Upload')}
      </Button>
    </>
  )
}

function TextForm({ toggleVisibility }: FormProps) {
  const { t } = useTranslation()
  const [text, setText] = useState<string>('')

  return (
    <>
      <Field isVertical className='-mt-4'>
        <Label className='md:w-full'>{t('Enter content')}</Label>
        <TextArea
          outlined
          value={text}
          placeholder='Enter your content here'
          onChange={(e) => setText(e.target.value)}
        />
      </Field>
      <Button className='mt-1' onClick={toggleVisibility} disabled={!text}>
        {t('Create app')}
      </Button>
    </>
  )
}

function EcommerceForm({ toggleVisibility }: FormProps) {
  const { t } = useTranslation()
  const [file, setFile] = useState<string>('')

  return (
    <>
      <Field isVertical className='-mt-4'>
        <Label className='md:w-full'>{t('Create your ecommerce app')}</Label>
        <UploadInput placeholder='Select your products' onChange={(e) => setFile(e.target.value)} />
      </Field>
      <Button className='mt-1' onClick={toggleVisibility} disabled={!file}>
        {t('Upload')}
      </Button>
    </>
  )
}

export default function GenerateNewAppModal({ closeModal, showAdd, type }: GenerateNewAppModalProps) {
  const { t } = useTranslation()
  const { show, toggleVisibility } = useModal()
  const [search, setSearch] = useState<string>('')
  const [templates, setTemplates] = useState<TemplateProps[]>(templateList)

  const handleSearch = (search: string) => {
    setSearch(search)
    if (!search) return setTemplates(templateList)
    setTemplates(templateList.filter((template) => template.name.toLowerCase().includes(search.toLowerCase())))
  }

  const appGenerator = appGeneratorData[type]

  const isNeedBigModal = type === 'template' || type === 'text'

  return (
    <>
      <Modal id={`org-detail-new-app-${type}-modal`} show={showAdd} onHide={closeModal}>
        <Modal.Header onCloseClick={closeModal}>{t(appGenerator.title)}</Modal.Header>
        <Modal.Body className={clsx(isNeedBigModal ? 'modal-md' : 'modal-sm')}>
          {type === 'template' && <TemplateForm search={search} templates={templates} handleSearch={handleSearch} />}
          {type === 'website' && <WebsiteForm toggleVisibility={toggleVisibility} />}
          {type === 'database' && <DatabaseForm toggleVisibility={toggleVisibility} />}
          {type === 'text' && <TextForm toggleVisibility={toggleVisibility} />}
          {type === 'ecommerce' && <EcommerceForm toggleVisibility={toggleVisibility} />}
        </Modal.Body>
      </Modal>
      {show && <LoadingGenerateNewAppModal show={show} toggleVisibility={toggleVisibility} />}
    </>
  )
}
